// src/app/settings/accruals/pages/employee-leave-detail/employee-leave-detail.component.ts

import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subject, forkJoin } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { SnackbarService } from '../../../../services/snackbar.service';

import { AccrualsService } from '../../accruals.service';
import {
    EmployeeUser,
    LeaveAccrualLedger,
    LeavePolicy,
    LeavePolicyStartDateRow,
    LeaveType,
} from '../../models/accruals.model';

interface ManualAdjustmentFormModel {
    leaveType: LeaveType | null;
    adjustmentMode: 'add' | 'subtract';
    hours: number | null;
    note: string;
}

interface AssignPolicyFormModel {
    leaveType: LeaveType | null;
    policyId: number | null;
    startDate: string;
}

@Component({
    selector: 'app-employee-leave-detail',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterModule],
    templateUrl: './employee-leave-detail.component.html',
    styleUrls: ['./employee-leave-detail.component.scss'],
})
export class EmployeeLeaveDetailComponent implements OnInit, OnDestroy {
    isLoading = false;
    errorMessage: string | null = null;

    employeeId: number | null = null;
    employee: EmployeeUser | null = null;

    allPolicies: LeavePolicy[] = [];
    employeeTransactions: LeaveAccrualLedger[] = [];
    employeePolicyHistory: LeavePolicyStartDateRow[] = [];

    manualAdjustmentModalOpen = false;
    isSavingManualAdjustment = false;
    manualAdjustmentForm: ManualAdjustmentFormModel = this.getEmptyManualAdjustmentForm();

    assignPolicyModalOpen = false;
    isAssigningPolicy = false;
    assignPolicyForm: AssignPolicyFormModel = this.getEmptyAssignPolicyForm();

    removePolicyConfirmOpen = false;
    isRemovingPolicy = false;
    selectedPolicyHistoryRowForRemoval: LeavePolicyStartDateRow | null = null;

    transactionPageSize = 5;
    transactionCurrentPage = 1;

    policyHistoryPageSize = 5;
    policyHistoryCurrentPage = 1;

    private readonly destroy$ = new Subject<void>();

    constructor(
        private readonly accrualsService: AccrualsService,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly cd: ChangeDetectorRef,
        private readonly snackbarService: SnackbarService,
    ) { }

    ngOnInit(): void {
        const id = Number(this.route.snapshot.paramMap.get('id'));

        if (!Number.isFinite(id) || id <= 0) {
            this.errorMessage = 'Invalid employee id.';
            return;
        }

        this.employeeId = id;
        this.loadEmployeeDetail();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();

        this.unlockBodyScroll();
    }

    private lockBodyScroll(): void {
        document.body.style.overflow = 'hidden';
    }

    private unlockBodyScroll(): void {
        document.body.style.overflow = '';
    }

    private syncBodyScrollLock(): void {
        if (
            this.manualAdjustmentModalOpen ||
            this.assignPolicyModalOpen ||
            this.removePolicyConfirmOpen
        ) {
            this.lockBodyScroll();
            return;
        }

        this.unlockBodyScroll();
    }

    get pageTitle(): string {
        if (!this.employee) return 'Employee Leave Detail';
        return `${this.employee.firstName} ${this.employee.lastName}`;
    }

    get vacationAvailableHours(): number {
        return this.employee?.employeeLeaveBalances.find((x) => x.leaveType === 'Vacation')?.availableHours ?? 0;
    }

    get vacationUsedHours(): number {
        return this.employee?.employeeLeaveBalances.find((x) => x.leaveType === 'Vacation')?.usedHours ?? 0;
    }

    get sickAvailableHours(): number {
        return this.employee?.employeeLeaveBalances.find((x) => x.leaveType === 'Sick')?.availableHours ?? 0;
    }

    get sickUsedHours(): number {
        return this.employee?.employeeLeaveBalances.find((x) => x.leaveType === 'Sick')?.usedHours ?? 0;
    }

    get assignablePolicies(): LeavePolicy[] {
        if (!this.assignPolicyForm.leaveType) {
            return [];
        }

        return this.allPolicies
            .filter((policy) => policy.leaveType === this.assignPolicyForm.leaveType)
            .sort((a, b) => a.name.localeCompare(b.name));
    }

    get activePolicyHistory(): LeavePolicyStartDateRow[] {
        return this.employeePolicyHistory.filter((row) => !row.isDeleted);
    }

    get deletedPolicyHistory(): LeavePolicyStartDateRow[] {
        return this.employeePolicyHistory
            .filter((row) => row.isDeleted)
            .sort((a, b) => String(b.deletedAt ?? '').localeCompare(String(a.deletedAt ?? '')));
    }

    get transactionTotalPages(): number {
        return Math.max(1, Math.ceil(this.employeeTransactions.length / this.transactionPageSize));
    }

    get pagedEmployeeTransactions(): LeaveAccrualLedger[] {
        const start = (this.transactionCurrentPage - 1) * this.transactionPageSize;
        return this.employeeTransactions.slice(start, start + this.transactionPageSize);
    }

    get canGoToPreviousTransactionPage(): boolean {
        return this.transactionCurrentPage > 1;
    }

    get canGoToNextTransactionPage(): boolean {
        return this.transactionCurrentPage < this.transactionTotalPages;
    }

    get policyHistoryTotalPages(): number {
        return Math.max(1, Math.ceil(this.activePolicyHistory.length / this.policyHistoryPageSize));
    }

    get pagedActivePolicyHistory(): LeavePolicyStartDateRow[] {
        const start = (this.policyHistoryCurrentPage - 1) * this.policyHistoryPageSize;
        return this.activePolicyHistory.slice(start, start + this.policyHistoryPageSize);
    }

    get canGoToPreviousPolicyHistoryPage(): boolean {
        return this.policyHistoryCurrentPage > 1;
    }

    get canGoToNextPolicyHistoryPage(): boolean {
        return this.policyHistoryCurrentPage < this.policyHistoryTotalPages;
    }

    loadEmployeeDetail(): void {
        if (!this.employeeId) {
            return;
        }

        this.isLoading = true;
        this.errorMessage = null;
        this.cd.detectChanges();

        forkJoin({
            users: this.accrualsService.getAllActiveUsers(),
            policies: this.accrualsService.getLeavePolicies(),
            transactions: this.accrualsService.getAllLeaveAccrualLedgers(),
            history: this.accrualsService.getLeavePolicyStartDates(this.employeeId),
        })
            .pipe(
                finalize(() => {
                    this.isLoading = false;
                    this.cd.detectChanges();
                }),
                takeUntil(this.destroy$),
            )
            .subscribe({
                next: ({ users, policies, transactions, history }) => {
                    const employee = (users ?? []).find((x) => x.id === this.employeeId) ?? null;

                    if (!employee) {
                        this.errorMessage = 'Employee not found.';
                        return;
                    }

                    this.employee = employee;
                    this.allPolicies = policies ?? [];

                    this.employeeTransactions = [...(transactions ?? [])]
                        .filter((x) => x.employee.id === this.employeeId)
                        .sort((a, b) => {
                            const dateCompare = String(b.effectiveDate).localeCompare(String(a.effectiveDate));
                            if (dateCompare !== 0) return dateCompare;
                            return String(b.createdAt ?? '').localeCompare(String(a.createdAt ?? ''));
                        });

                    this.employeePolicyHistory = [...(history ?? [])].sort((a, b) => {
                        const startCompare = String(b.startDate).localeCompare(String(a.startDate));
                        if (startCompare !== 0) return startCompare;
                        return String(b.createdAt ?? '').localeCompare(String(a.createdAt ?? ''));
                    });

                    this.transactionCurrentPage = 1;
                    this.policyHistoryCurrentPage = 1;

                    this.cd.detectChanges();
                },
                error: () => {
                    this.errorMessage = 'Failed to load employee leave detail.';
                    this.cd.detectChanges();
                },
            });
    }

    goBack(): void {
        this.router.navigate(['/settings/accruals/employee-leave-balances']);
    }

    openManualAdjustmentModal(): void {
        this.manualAdjustmentForm = this.getEmptyManualAdjustmentForm();

        this.manualAdjustmentModalOpen = true;
        this.syncBodyScrollLock();

        this.cd.detectChanges();
    }

    closeManualAdjustmentModal(): void {
        this.manualAdjustmentModalOpen = false;
        this.manualAdjustmentForm = this.getEmptyManualAdjustmentForm();

        this.syncBodyScrollLock();

        this.cd.detectChanges();
    }

    saveManualAdjustment(): void {
        if (!this.employeeId) {
            return;
        }

        if (
            !this.manualAdjustmentForm.leaveType ||
            !this.manualAdjustmentForm.hours ||
            this.manualAdjustmentForm.hours <= 0 ||
            !this.manualAdjustmentForm.note.trim()
        ) {
            this.snackbarService.error('Complete all manual adjustment fields before saving');
            return;
        }

        const normalizedHours =
            this.manualAdjustmentForm.adjustmentMode === 'subtract'
                ? -Math.abs(this.manualAdjustmentForm.hours)
                : Math.abs(this.manualAdjustmentForm.hours);

        this.isSavingManualAdjustment = true;
        this.cd.detectChanges();

        this.accrualsService
            .createManualAdjustment({
                employeeId: this.employeeId,
                leaveType: this.manualAdjustmentForm.leaveType,
                hours: normalizedHours,
                note: this.manualAdjustmentForm.note.trim(),
                timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
            })
            .pipe(
                finalize(() => {
                    this.isSavingManualAdjustment = false;
                    this.cd.detectChanges();
                }),
                takeUntil(this.destroy$),
            )
            .subscribe((success) => {
                if (!success) {
                    this.snackbarService.error('Failed to save manual adjustment');
                    return;
                }

                this.closeManualAdjustmentModal();
                this.snackbarService.success('Manual adjustment saved successfully');
                this.loadEmployeeDetail();
            });
    }

    openAssignPolicyModal(): void {
        this.assignPolicyForm = this.getEmptyAssignPolicyForm();

        this.assignPolicyModalOpen = true;
        this.syncBodyScrollLock();

        this.cd.detectChanges();
    }

    closeAssignPolicyModal(): void {
        this.assignPolicyModalOpen = false;
        this.assignPolicyForm = this.getEmptyAssignPolicyForm();

        this.syncBodyScrollLock();

        this.cd.detectChanges();
    }

    onAssignLeaveTypeChange(value: LeaveType | null): void {
        this.assignPolicyForm.leaveType = value;
        this.assignPolicyForm.policyId = null;
    }

    saveAssignedPolicy(): void {
        if (!this.employeeId) {
            return;
        }

        if (
            !this.assignPolicyForm.leaveType ||
            !this.assignPolicyForm.policyId ||
            !this.assignPolicyForm.startDate
        ) {
            this.snackbarService.error('Complete all policy assignment fields before saving');
            return;
        }

        this.isAssigningPolicy = true;
        this.cd.detectChanges();

        this.accrualsService
            .assignEmployeesToPolicy(this.assignPolicyForm.policyId, {
                employeeIds: [this.employeeId],
                startDate: this.assignPolicyForm.startDate,
            })
            .pipe(
                finalize(() => {
                    this.isAssigningPolicy = false;
                    this.cd.detectChanges();
                }),
                takeUntil(this.destroy$),
            )
            .subscribe((success) => {
                if (!success) {
                    this.snackbarService.error('Failed to assign policy');
                    return;
                }

                this.closeAssignPolicyModal();
                this.snackbarService.success('Policy assigned successfully');
                this.loadEmployeeDetail();
            });
    }

    openRemovePolicyConfirm(row: LeavePolicyStartDateRow): void {
        this.selectedPolicyHistoryRowForRemoval = row;

        this.removePolicyConfirmOpen = true;
        this.syncBodyScrollLock();

        this.cd.detectChanges();
    }

    closeRemovePolicyConfirm(force: boolean = false): void {
        if (this.isRemovingPolicy && !force) {
            return;
        }

        this.removePolicyConfirmOpen = false;
        this.selectedPolicyHistoryRowForRemoval = null;

        this.syncBodyScrollLock();

        this.cd.detectChanges();
    }

    confirmRemovePolicyHistoryRow(): void {
        const row = this.selectedPolicyHistoryRowForRemoval;

        if (!row?.id || this.isRemovingPolicy) {
            return;
        }

        this.isRemovingPolicy = true;
        this.cd.detectChanges();

        this.accrualsService
            .deleteLeavePolicyStartDate(row.id)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.isRemovingPolicy = false;
                    this.cd.detectChanges();
                }),
            )
            .subscribe({
                next: (success) => {
                    if (!success) {
                        this.snackbarService.error('Failed to remove employee from policy');
                        return;
                    }

                    this.removePolicyConfirmOpen = false;
                    this.selectedPolicyHistoryRowForRemoval = null;

                    this.syncBodyScrollLock();

                    this.snackbarService.success('Employee removed from policy successfully');
                    this.loadEmployeeDetail();
                },
                error: (err) => {
                    console.error('Remove policy request failed:', err);
                    this.snackbarService.error('Failed to remove employee from policy');
                },
            });
    }

    goToPreviousTransactionPage(): void {
        if (!this.canGoToPreviousTransactionPage) return;
        this.transactionCurrentPage--;
    }

    goToNextTransactionPage(): void {
        if (!this.canGoToNextTransactionPage) return;
        this.transactionCurrentPage++;
    }

    goToPreviousPolicyHistoryPage(): void {
        if (!this.canGoToPreviousPolicyHistoryPage) return;
        this.policyHistoryCurrentPage--;
    }

    goToNextPolicyHistoryPage(): void {
        if (!this.canGoToNextPolicyHistoryPage) return;
        this.policyHistoryCurrentPage++;
    }

    getBalanceClass(value: number): string {
        if (value > 0) return 'positive';
        if (value < 0) return 'negative';
        return 'neutral';
    }

    getLeaveTypeClass(type: string | null | undefined): string {
        return String(type ?? '').toLowerCase() === 'sick' ? 'sick' : 'vacation';
    }

    getHoursClass(hours: number): string {
        if (hours > 0) return 'positive';
        if (hours < 0) return 'negative';
        return 'neutral';
    }

    getEntryTypeClass(entryType: string | null | undefined): string {
        const normalized = String(entryType ?? '').toLowerCase();

        if (normalized.includes('manual')) return 'manual';
        if (normalized.includes('accrual')) return 'accrual';
        if (normalized.includes('approved')) return 'approved';
        if (normalized.includes('cancelled')) return 'cancelled';
        if (normalized.includes('rejected')) return 'rejected';

        return 'default';
    }

    getNormalizedLeaveType(value: number | string | null | undefined): LeaveType {
        const normalized = String(value ?? '').toLowerCase();

        if (normalized === '1' || normalized === 'vacation') {
            return 'Vacation';
        }

        return 'Sick';
    }

    getPolicyAccrualFrequency(policy: { accrualFrequency?: string | number | null } | null | undefined): string {
        return String(policy?.accrualFrequency ?? '-');
    }

    trackByTransactionId(_: number, row: LeaveAccrualLedger): number {
        return row.id;
    }

    trackByHistoryId(_: number, row: LeavePolicyStartDateRow): number {
        return row.id;
    }

    private getEmptyManualAdjustmentForm(): ManualAdjustmentFormModel {
        return {
            leaveType: null,
            adjustmentMode: 'add',
            hours: null,
            note: '',
        };
    }

    private getEmptyAssignPolicyForm(): AssignPolicyFormModel {
        return {
            leaveType: null,
            policyId: null,
            startDate: '',
        };
    }
}